import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    borders, colors, fontSizes, fontWeights, lineHeights, space, radii, mediaQueries
} from 'style/config';
import Checkbox from 'components/Inputs/Checkbox/Checkbox';
import { Link } from 'components/ui';
import localeUtils from 'utils/LanguageLocale';
import Empty from 'constants/empty';
import BundleWidgetEN from 'components/ProductPage/PdpBundling/BundleWidget/locales/BundleWidget_en_US';
import BundleWidgetFR from 'components/ProductPage/PdpBundling/BundleWidget/locales/BundleWidget_fr_CA';
import { getChangeOptionNoun } from 'components/ProductPage/PdpBundling/BundleWidget/utils/buildBundleProducts';

const getText = localeUtils.getLocalisedDataFromResource(BundleWidgetEN, BundleWidgetFR);

const VARIATION_NOUN_KEY = {
    Color: 'variationColor',
    Size: 'variationSize',
    Scent: 'variationScent',
    Option: 'variationOption'
};

const PREFIXED_VARIATION_TYPES = new Set(['Color', 'Size', 'Scent']);

const getSkuDescription = (variationValue, variationNoun, localizedVariationNoun) =>
    variationValue &&
    (PREFIXED_VARIATION_TYPES.has(variationNoun) ? getText('skuDescription', [localizedVariationNoun, variationValue]) : variationValue);

const BundleProductCard = ({
    product, isCurrentProduct, isChecked, onCheckboxChange, onChangeSkuClick
}) => {
    const {
        brandName,
        variationType,
        productName,
        displayName,
        skuImages,
        listPrice,
        salePrice,
        variationValue,
        productId,
        variationId,
        strategyId,
        itemPrice
    } = product || Empty.Object;
    const imageUrl = skuImages?.imageUrl || skuImages?.['image250'];
    // FER-889: the hero SKU's displayName is "<skuId> <variationValue>", so prefer the product name.
    const cardProductName = productName || displayName;
    const allowSkuChange = !isCurrentProduct && variationType;
    const isOnSale = Boolean(salePrice);
    const displayPrice = salePrice || listPrice;

    const variationNoun = variationType ? getChangeOptionNoun(variationType) : undefined;
    const variationNounKey = VARIATION_NOUN_KEY[variationNoun];
    const localizedVariationNoun = (variationNounKey && getText(variationNounKey)) || variationNoun;
    const skuDescription = getSkuDescription(variationValue, variationNoun, localizedVariationNoun);

    const constructorItemAttributes = strategyId
        ? {
            'data-cnstrc-item': 'recommendation',
            'data-cnstrc-item-id': productId,
            'data-cnstrc-item-name': productName,
            'data-cnstrc-item-variation-id': variationId,
            'data-cnstrc-strategy-id': strategyId,
            'data-cnstrc-item-price': itemPrice
        }
        : Empty.Object;

    const renderChangeLink = css =>
        allowSkuChange && (
            <Link
                css={css}
                onClick={onChangeSkuClick}
            >
                {getText('change')} {localizedVariationNoun}
            </Link>
        );

    const renderPrice = stackCss =>
        displayPrice && (
            <div css={stackCss}>
                <span css={[styles.price, styles.currentPrice, isOnSale && styles.salePrice]}>{displayPrice}</span>
                {isOnSale && listPrice && <span css={[styles.price, styles.originalPrice]}>{listPrice}</span>}
            </div>
        );

    return (
        <div
            css={styles.card}
            {...constructorItemAttributes}
        >
            <Checkbox
                checked={isCurrentProduct ? true : Boolean(isChecked)}
                onChange={isCurrentProduct ? () => {} : onCheckboxChange}
                aria-label={cardProductName}
                aria-disabled={isCurrentProduct || undefined}
                paddingY={0}
            />
            <div css={styles.contentWrapper}>
                {imageUrl && (
                    <img
                        src={imageUrl}
                        alt={cardProductName}
                        css={styles.image}
                    />
                )}
                <div css={styles.info}>
                    {brandName && (
                        <span
                            css={styles.brand}
                            dangerouslySetInnerHTML={{ __html: brandName }}
                        />
                    )}
                    <span
                        css={styles.productName}
                        dangerouslySetInnerHTML={{ __html: cardProductName || '' }}
                    />
                    {skuDescription && <span css={styles.variation}>{skuDescription}</span>}
                    {isCurrentProduct && <span css={styles.badge}>{getText('currentProduct')}</span>}
                </div>
                <div css={styles.priceColumn}>
                    {renderPrice(styles.priceStack)}
                    {renderChangeLink(styles.changeLinkMobile)}
                </div>
                <div css={styles.bottomSection}>
                    {isCurrentProduct && <span css={styles.badgeDesktop}>{getText('currentProduct')}</span>}
                    {renderChangeLink(styles.changeLinkDesktop)}
                    {renderPrice(styles.priceStackDesktop)}
                </div>
            </div>
        </div>
    );
};

const styles = {
    card: {
        display: 'flex',
        gap: 0,
        alignItems: 'center',
        padding: `${space[3]}px ${space[4]}px`,
        borderBottom: `${borders[1]} ${colors.lightGray}`,
        width: '100%',
        [mediaQueries.sm]: {
            gap: space[1],
            padding: 0,
            borderBottom: 'none',
            height: '100%'
        }
    },
    contentWrapper: {
        display: 'contents',
        [mediaQueries.sm]: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minWidth: 0,
            gap: space[2],
            alignSelf: 'stretch'
        }
    },
    image: {
        width: 60,
        height: 60,
        objectFit: 'cover',
        [mediaQueries.sm]: {
            width: 108,
            height: 108
        }
    },
    info: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        flex: 1,
        [mediaQueries.sm]: {
            flex: 'none'
        }
    },
    priceColumn: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        alignSelf: 'stretch',
        justifyContent: 'space-between',
        [mediaQueries.sm]: { display: 'none' }
    },
    bottomSection: {
        display: 'none',
        [mediaQueries.sm]: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: space[4],
            marginTop: 'auto'
        }
    },
    brand: {
        fontSize: fontSizes.xs,
        fontWeight: fontWeights.bold,
        lineHeight: lineHeights.tight,
        [mediaQueries.sm]: {
            fontSize: fontSizes.sm
        }
    },
    productName: {
        fontSize: fontSizes.sm,
        lineHeight: lineHeights.tight,
        whiteSpace: 'wrap',
        [mediaQueries.sm]: {
            fontSize: fontSizes.base
        }
    },
    variation: {
        fontSize: fontSizes.xs,
        lineHeight: lineHeights.tight,
        color: colors.gray
    },
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        alignSelf: 'flex-start',
        height: 18,
        backgroundColor: colors.nearWhite,
        borderRadius: radii[2],
        padding: `0 ${space[2]}px`,
        fontSize: fontSizes.sm,
        lineHeight: lineHeights.tight,
        color: colors.gray,
        marginTop: space[2],
        [mediaQueries.sm]: { display: 'none' }
    },
    badgeDesktop: {
        display: 'none',
        [mediaQueries.sm]: {
            display: 'inline-flex',
            alignItems: 'center',
            alignSelf: 'flex-start',
            height: 18,
            backgroundColor: colors.nearWhite,
            borderRadius: radii[2],
            padding: `0 ${space[2]}px`,
            fontSize: fontSizes.sm,
            lineHeight: lineHeights.tight,
            color: colors.gray
        }
    },
    price: {
        fontWeight: fontWeights.bold,
        lineHeight: lineHeights.tight,
        textAlign: 'right'
    },
    currentPrice: {
        fontSize: fontSizes.md
    },
    salePrice: {
        color: colors.red
    },
    originalPrice: {
        fontSize: fontSizes.base,
        color: colors.gray,
        textDecoration: 'line-through'
    },
    priceStack: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end'
    },
    priceStackDesktop: {
        display: 'none',
        [mediaQueries.sm]: {
            display: 'flex',
            alignItems: 'baseline',
            gap: space[1]
        }
    },
    changeLinkMobile: {
        fontSize: fontSizes.sm,
        color: colors.link,
        textAlign: 'right',
        [mediaQueries.sm]: { display: 'none' }
    },
    changeLinkDesktop: {
        display: 'none',
        [mediaQueries.sm]: {
            display: 'block',
            color: colors.link
        }
    }
};

export default wrapFunctionalComponent(BundleProductCard, 'BundleProductCard');
